import { CommonModule } from "@angular/common";
import { Component, computed, effect, inject, input, Signal, Type, ViewChild } from '@angular/core';
import { TabsModule } from "primeng/tabs";
import { MarkdownComponent } from "ngx-markdown";

// EZ
import { DocClassEntry } from "../../types/component-docs.model";
import { EzBaseComponent } from "../../components/ez-base.component";
import { UiLibraryModule } from "../../template-ui.module";
import { LibraryDocService } from "../library-doc.service";
import { EzPanelComponent } from "../../components/ez-panel/panel/panel.component";
import { TilePanelComponent } from "../../components/tile-panel/tile-panel.component";
import { TileConfigItem } from "../../types/tile-panel.types";
import { EZ_LIB_SHOWCASE_SAMPLE_COMPONENTS } from "../showcase-components.gen";
import { APP_SHOWCASE_SAMPLE_COMPONENT_EXTENSIONS_TOKEN, APP_SHOWCASE_SAMPLE_COMPONENTS_TOKEN } from "@template-ui/tokens";
import { resolveShowcaseLinkTokens } from "../showcase-link-tokens";
import { EzShowcaseSampleComponentsExtension } from "@template-ui/types";

type SampleComponent = {
  className: string
  isExternal?: boolean
  component?: Type<Component> | Type<unknown>
  componentUrl?: string
  templateUrl?: string
  ts?: string
  html?: string
  scss?: string
  showcaseTitle?: string
  showcaseDesc?: string
}

/**
 * This is the component with the dashboard tiles and panels for each sample component specified
 * in the DocMeta for a given component.
 *
 */
@Component({
    selector: 'ui-showcase-sample, ez-showcase-sample',
    imports: [
        CommonModule,
        // Prime
        TabsModule,
        // EZ
        UiLibraryModule,
        EzPanelComponent,
        MarkdownComponent,
        TilePanelComponent
    ],
    templateUrl: './showcase-sample.component.html',
    styleUrl: './showcase-sample.component.scss'
})
export class ShowcaseSampleComponent extends EzBaseComponent {
  private readonly navigationEndSig = this.createNavigationEndSignal();
  private suppressTileClickedEvent = false;
  private copiedBlockKey?: string;
  private copiedBlockResetTimer?: ReturnType<typeof setTimeout>;

  @ViewChild('tilePanel') tilePanel?: TilePanelComponent

  selectedDoc = input<Signal<DocClassEntry | undefined> | DocClassEntry | undefined>(undefined);
  doc = computed(() => {
    const value = this.selectedDoc();
    return typeof value === "function" ? value() : value;
  });

  showcaseService = inject(LibraryDocService);

  selectedPanelName?: string;

  component: Type<Component> | undefined; // = EzNoShowcaseComponent;

  sampleTilesConfig: TileConfigItem[] = [];

  private appShowcaseSampleComponents = inject(APP_SHOWCASE_SAMPLE_COMPONENTS_TOKEN, { optional: true });
  private appShowcaseSampleComponentExtensions =
    inject(APP_SHOWCASE_SAMPLE_COMPONENT_EXTENSIONS_TOKEN, { optional: true }) as EzShowcaseSampleComponentsExtension[] | null;

  sampleComponents = computed(() => {
    const doc = this.doc();

    const libName = doc?.libName;

    const rtn: SampleComponent[] = [];

    const componentNames = doc?.showcaseComponentNames;
    if(libName && componentNames)
    {
      componentNames.forEach((name:string, index:number) => {
        const code = this.showcaseService.getCodeFor(libName, name);

        if(code)
        {
          const isExternal = !this.showcaseService.hasCodeForInLibrary(libName, name);
          const samp: SampleComponent = {
            className: name,
            isExternal,
            component: this.getComponentFromString(name),
            showcaseTitle: code.showcaseTitle || "",
            showcaseDesc: code.showcaseDesc || "",

            // New way - not using this yet.
            componentUrl: code.urls?.componentUrl,
            templateUrl: code.urls?.templateUrl,

            // Old way
            ts: this.removeDocMetaFromSampleTs(code.files?.ts || '') || "FOUND",
            html: code.files?.html,
            scss: code.files?.scss

          }

          this.tabValues[name]="html";

          this.dlog(`Adding sample ${samp.className}`);
          rtn.push(samp);
        }
        else
        {
          // Show a disabled tile with the name just to point out that the sample wasn't found
            const samp: SampleComponent = {
              className: name,
              ts: "NOT_FOUND"
            };

            rtn.push(samp);
        }

      });
    }

    this.Prism.highlightAll();

    return rtn;
  });


  removeDocMetaFromSampleTs(sourceText: string): string
  {
    // Remove DocMeta import lines (alias or relative paths with/without template-ui segment)
    sourceText = sourceText.replace(
      /^.*import\s*\{\s*DocMeta\s*\}\s*from\s*["'](?:@template-ui\/decorators(?:\/decorators)?|(?:\.\.\/)+(?:template-ui\/)?decorators(?:\/decorators)?)["'];?.*\r?\n?/gm,
      ''
    );

    // Remove the @DocMeta()
    sourceText = sourceText.replace(/^.*@DocMeta\s*\(\s*\{[\s\S]*?\}\s*\).*\r?\n?/gm, '');

    return sourceText;
  }

  tabValues: Record<string, string> = {};

  onTabChange()
  {
    setTimeout(() => {
      this.Prism.highlightAll();
    }, 10);

  }

  constructor()
  {
    super();
    this.thisClassName = 'ShowcaseSampleComponent';

    effect(() => {
      this.navigationEndSig();
      const samples = this.sampleComponents();

      if (!samples.length) {
        return;
      }

      const selectedFromQuery = this.getSampleIdFromQuery(samples);
      if (selectedFromQuery && selectedFromQuery !== this.selectedPanelName) {
        this.selectSample(selectedFromQuery, false);
      }
    });

    effect(()=>{

      const doc = this.doc();

      requestAnimationFrame(() => {
        //this.component = comp;

        this.cd.detectChanges();

        this.Prism.highlightAll();
      });

      // Configure the Tile Panel
      const samples = this.sampleComponents();

      const sampleTiles: TileConfigItem[] = [];

      samples.forEach((s: SampleComponent) => {

        sampleTiles.push({
          id: s.className,
          textHtml: s.showcaseTitle || s.className,
          cssClass: s.isExternal ? "external-showcase-sample-tile" : undefined,
          disabled: s.ts == "NOT_FOUND"
        });
      });

      this.sampleTilesConfig = [...sampleTiles];

      // In a timeout cuz it seems to take a bit for the tilesConfig to get the current values
      // from the line above.
      setTimeout(() => {
        if(sampleTiles[0])
        {
            const selectedSampleId = this.getSampleIdFromQuery(samples) ?? sampleTiles[0].id;
            this.selectSample(selectedSampleId, true);
        }
      }, 200);

    });
  }

  /** Handles dashboard tile clicks and syncs the selected sample to the URL query param. */
  onSampleTileClicked(sampleId: string): void {
    if (this.suppressTileClickedEvent) {
      return;
    }
    this.selectSample(sampleId, true);
  }

  /** Routes internal markdown links through Angular Router to avoid full page reloads. */
  protected onMarkdownLinkClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const anchor = target?.closest('a');
    const href = anchor?.getAttribute('href');
    if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
      return;
    }

    const resolvedUrl = new URL(href, window.location.origin);
    if (resolvedUrl.origin !== window.location.origin) {
      return;
    }

    const normalizedBaseHref = this.baseHref.endsWith('/') ? this.baseHref.slice(0, -1) : this.baseHref;
    let appPath = resolvedUrl.pathname;
    if (normalizedBaseHref && normalizedBaseHref !== '/' && appPath.startsWith(`${normalizedBaseHref}/`)) {
      appPath = appPath.slice(normalizedBaseHref.length);
    }

    event.preventDefault();
    void this.router.navigateByUrl(`${appPath}${resolvedUrl.search}${resolvedUrl.hash}`);
  }



  getComponentFromString(componentName: string): Type<unknown> | undefined {
    // const allSampleComponents = new Map<string, Type<unknown>>([
    //   ...EZ_LIB_SHOWCASE_SAMPLE_COMPONENTS,
    //   ...this.appShowcaseSampleComponents,
    // ]);
    // return allSampleComponents.get(componentName);

    const merged = new Map<string, Type<unknown>>(EZ_LIB_SHOWCASE_SAMPLE_COMPONENTS);

    this.appShowcaseSampleComponentExtensions?.forEach((extension) => {
      extension.components?.forEach((component, name) => {
        if (!merged.has(name)) {
          merged.set(name, component);
        }
      });
    });

    // Only add app entries that aren’t already present
    if(this.appShowcaseSampleComponents)
    {
      this.appShowcaseSampleComponents.forEach((component, name) => {
        if (!merged.has(name)) merged.set(name, component);
      });
    }

    return merged.get(componentName);
  }

  copyToClipboard(content?: string, blockKey?: string): void {
    if(!content) return;

    navigator.clipboard.writeText(content).then(() => {
      if (blockKey) {
        this.setCopiedState(blockKey);
      }

      // Optional: show toast or visual feedback
      //this.devMessage("Copied to clipboard");

      /* eslint-disable no-restricted-syntax */
      console.info('Copied to clipboard');  // eslint-disable-line no-console
      /* eslint-enable no-restricted-syntax */

    }).catch(err => console.error(`Failed to writeText to clipboard: ${err}`));;
  }

  getCopyTooltip(blockKey: string): string {
    return this.copiedBlockKey === blockKey ? "Copied!" : "Copy code";
  }

  getCopyIcon(blockKey: string): string {
    return this.copiedBlockKey === blockKey ? "pi-check" : "pi-copy";
  }

  getCopySeverity(blockKey: string): "success" | "contrast" {
    return this.copiedBlockKey === blockKey ? "success" : "contrast";
  }

  /** Selects a sample tile/panel and optionally updates the URL query param. */
  private selectSample(sampleId: string, syncUrl: boolean): void {
    if (this.selectedPanelName === sampleId) {
      this.syncTilePanelSelection(sampleId);
      if (syncUrl) {
        this.syncSampleQueryParam(sampleId);
      }
      return;
    }

    this.selectedPanelName = sampleId;
    this.syncTilePanelSelection(sampleId);

    if (syncUrl) {
      this.syncSampleQueryParam(sampleId);
    }
  }

  /** Applies selected sample state to the tile panel so the active tile is visibly highlighted. */
  private syncTilePanelSelection(sampleId: string): void {
    if (!this.tilePanel) {
      return;
    }

    this.suppressTileClickedEvent = true;
    try {
      this.tilePanel.setSelectedTile(sampleId);
    } finally {
      this.suppressTileClickedEvent = false;
    }
  }

  /** Resolves a sample id from the current `sample` query param if present and valid. */
  private getSampleIdFromQuery(samples: SampleComponent[]): string | undefined {
    const sampleParam = this.activatedRoute.snapshot.queryParamMap.get("sample");
    if (!sampleParam) {
      return undefined;
    }

    const normalizedParam = sampleParam.trim().toLowerCase();
    const match = samples.find((sample) => this.getSampleSlug(sample) === normalizedParam);
    return match?.className;
  }

  /** Updates `sample` query param to reflect the selected sample slug. */
  private syncSampleQueryParam(sampleId: string): void {
    const sample = this.sampleComponents().find((item) => item.className === sampleId);
    if (!sample) {
      return;
    }

    const sampleSlug = this.getSampleSlug(sample);
    if (this.activatedRoute.snapshot.queryParamMap.get("sample") === sampleSlug) {
      return;
    }

    void this.router.navigate([], {
      queryParams: { sample: sampleSlug },
      queryParamsHandling: "merge",
      fragment: this.activatedRoute.snapshot.fragment ?? undefined,
      replaceUrl: true,
    });
  }

  /** Generates a stable URL slug for sample query-parameter routing. */
  private getSampleSlug(sample: SampleComponent): string {
    const source = (sample.showcaseTitle || sample.className).replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    return source
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /** Resolves internal showcase markdown link tokens for sample descriptions. */
  protected resolveSampleDescription(markdown: string | undefined): string {
    return resolveShowcaseLinkTokens(markdown, this.doc()?.libName);
  }

  private setCopiedState(blockKey: string): void {
    this.copiedBlockKey = blockKey;

    if (this.copiedBlockResetTimer) {
      clearTimeout(this.copiedBlockResetTimer);
    }

    this.copiedBlockResetTimer = setTimeout(() => {
      if (this.copiedBlockKey === blockKey) {
        this.copiedBlockKey = undefined;
      }
    }, 1400);
  }
}

